import { Form } from 'redux-form'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Button, Col, Grid } from 'gu-ui-kit'
import { useMediaPlanner } from 'modules/MediaPlanner/hooks/useMediaPlanner'
import { useDispatch, useSelector } from 'react-redux'
import GridSpinner from 'components/UI/GridSpinner'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import useQueryParams from 'utils/hooks/useQueryParams'
import { FormField } from 'modules/Forms/MediaPlanner/subcomponents/FormField'
import styles from './mediaplanner-form.module.scss'

const MediaPlannerForm = ({
  handleSubmit,
  form,
  autofill,
  change,
  className = '',
  config,
  benchmark,
  reset,
}) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const [initialized, setInitialized] = useState(false)
  const { query, setQuery } = useQueryParams()
  const { getData, setData, resetData } = useMediaPlanner()
  const dispatch = useDispatch()

  const {
    mediaPlanner = {},
    form: { values: formValues = {}, asyncErrors: formErrors = {} } = {},
  } = useSelector((state) => ({
    mediaPlanner: state[form],
    form: state.form?.[form] ?? {},
  }))

  const { target_results: { loading = false } = {} } = mediaPlanner

  useEffect(() => {
    setData(
      Object.entries(config.fields).reduce((acc, [key, { options = [] }]) => {
        return {
          ...acc,
          [key]: {
            data: options,
          },
        }
      }, {}),
      [],
    )

    const { apiMethod, apiDependencies } = config?.['target_results'] ?? {}

    const params = {
      type: 'target_results',
      method: apiMethod,
      benchmark,
    }

    const dependencies = apiDependencies.reduce(
      (acc, dependency) =>
        query?.[dependency]?.length
          ? {
              ...acc,
              [dependency]: query[dependency],
            }
          : acc,
      {},
    )

    if (Object.keys(dependencies).length === apiDependencies.length) {
      Object.entries(dependencies).forEach(([key, value]) => (params[key] = value))

      getData(params)
    }
  }, [config, setData, getData, benchmark])

  useEffect(() => {
    const fields = Object.entries(config.fields)

    if (initialized || !fields.length) {
      return
    }

    fields.forEach(([key, { sub_field = null, options = [] }]) => {
      if (query?.[key]) {
        autofill(key, query[key])
      }

      if (sub_field) {
        if (query?.[sub_field.name]) {
          autofill(sub_field.name, query[sub_field.name])
        }

        if (query?.[key]?.length > 0) {
          autofill(
            `${sub_field.name}_min`,
            options?.find((option) => option.value === query[key])?.min,
          )
          autofill(
            `${sub_field.name}_max`,
            options?.find((option) => option.value === query[key])?.max,
          )
        }
      }
    })
    setInitialized(true)
  }, [autofill, initialized, config, query])

  useEffect(() => {
    Object.entries(mediaPlanner).forEach(([key, { data = [] }]) => {
      if (data.length === 1) {
        // если массив опций состоит из одного элемента, то делаем его активным
        autofill(
          key,
          config.fields[key].multiple ? ['' + data[0]?.term_group_id] : '' + data[0]?.term_group_id,
        )
      }

      if (data.length > 0 && config.fields[key].selected && !formValues?.[key].length) {
        // если есть атрибут selected, то делаем все варианты активными
        autofill(
          key,
          data.map(({ term_group_id }) => '' + term_group_id),
        )
      }
    })
  }, [mediaPlanner, config])

  useEffect(() => () => resetData(), [])

  const resetForm = useCallback(() => {
    dispatch(reset(form))
    setQuery({})
    resetData()
    setData(
      Object.entries(config.fields).reduce((acc, [key, { options = [] }]) => {
        return {
          ...acc,
          [key]: {
            data: options,
          },
        }
      }, {}),
      [],
    )
  }, [form, setData, resetData, setQuery, config])

  const submit = useCallback(
    (values) => {
      const { apiMethod = '', apiDependencies = [] } = config?.['target_results'] ?? {}

      const params = apiDependencies.reduce(
        (acc, dependency) => ({ ...acc, [dependency]: values[dependency] }),
        {},
      )

      setQuery(params)

      getData({
        ...params,
        type: 'target_results',
        method: apiMethod,
        benchmark,
      })
    },
    [config, benchmark],
  )

  const handleChange = useCallback(
    (name) => {
      Object.entries(config.fields)
        .filter(([, { type }]) => type === 'selector')
        .forEach(([key, { dependencies = [] }]) => {
          const hasDependency = dependencies.includes(name)

          if (hasDependency) {
            setData({
              [key]: {
                data: config.fields[key].options,
              },
            })
            autofill(key, config.fields[key].value)
          }
        })
    },
    [config, setData, autofill],
  )

  return (
    <Form
      className={className}
      onSubmit={handleSubmit(submit)}
    >
      <Grid
        cols={!breakpoint.fb ? 1 : !breakpoint.mdl ? 3 : !breakpoint.fhd ? 4 : 5}
        gap={16}
        direction={'row'}
        wrap={true}
      >
        {Object.entries(config.fields)
          .filter(([, { type }]) => type === 'selector')
          .map(([key, props]) => (
            <Col key={key}>
              <FormField
                name={key}
                {...props}
                options={
                  mediaPlanner?.[key]?.data?.map(
                    ({ value, label, id, name, term_group_id, ...rest }) => ({
                      value: '' + (value || term_group_id || id),
                      label: label || name,
                      ...rest,
                    }),
                  ) ?? []
                }
                autofill={autofill}
                change={change}
                formValues={formValues}
                formErrors={formErrors}
                getData={getData}
                loading={!!mediaPlanner?.[key]?.loading}
                loaded={!!mediaPlanner?.[key]?.loaded}
                onChange={handleChange}
                benchmark={benchmark}
              />
            </Col>
          ))}

        <Col>
          <Grid
            direction={!breakpoint.fb ? 'row-reverse' : 'row'}
            wrap={false}
            gap={16}
            className={styles['media-planner__buttons']}
          >
            <Button
              disabled={loading}
              type={'submit'}
              variant={'primary'}
              size={'l'}
              startIcon={loading ? <GridSpinner width={24} /> : null}
            >
              {t('common::buttons.calculate')}
            </Button>
            <Button
              variant={'ghost'}
              size={'l'}
              onClick={resetForm}
            >
              {t('common::buttons.reset')}
            </Button>
          </Grid>
        </Col>
      </Grid>
    </Form>
  )
}

MediaPlannerForm.propTypes = {
  handleSubmit: PropTypes.func,
  form: PropTypes.string,
  reset: PropTypes.func,
  autofill: PropTypes.func,
  change: PropTypes.func,
  focus: PropTypes.func,
  asyncValidate: PropTypes.func,
  blur: PropTypes.func,
  dirty: PropTypes.bool,
  valid: PropTypes.bool,
  networkId: PropTypes.string,
  className: PropTypes.string,
  config: PropTypes.shape({
    fields: PropTypes.object,
    target_results: PropTypes.shape({
      multiple: PropTypes.bool,
      sub_field: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.object]),
      value: PropTypes.string,
    }),
  }),
  benchmark: PropTypes.string,
}

export default MediaPlannerForm
